import React from "react";
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from "@material-ui/core";

const CustomerLoyaltyAPIDocumentation = () => {
  return (
    <Box p={4}>
      <Typography variant="h4" gutterBottom>
        Customer Loyalty API Documentation
      </Typography>
      <Typography variant="h6" gutterBottom>
        2.0
      </Typography>
      <Typography variant="body1" paragraph>
        Welcome to the Loyalty API Documentation. This document is designed to help you understand and integrate with our Loyalty API for seamless interaction and deployment.
      </Typography>
      <Typography variant="body1" paragraph>
        We've listed all the APIs, their required parameters, and their example requests and responses on the right for easy understanding.
      </Typography>
      <Typography variant="body1" paragraph>
        Our APIs are based around REST architecture and use the basic HTTP request methods. Our APIs accept JSON-encoded body requests and return data in the same form.
      </Typography>

      <Typography variant="h5" gutterBottom>
        Getting Started
      </Typography>
      <Typography variant="body1" paragraph>
        First, you need to register with Customer service and create an account.
      </Typography>

      <Typography variant="h5" gutterBottom>
        Authentication
      </Typography>
      <Typography variant="body1" paragraph>
        When logging in, you'll receive a JWT token that is required to access other protected resources. This token is generated at the time of login and must be included in the Authorization header for subsequent API requests.
      </Typography>

      <Typography variant="h5" gutterBottom>
        Document and API Usage Guidelines
      </Typography>
      <Typography variant="body1" paragraph>
        Our APIs use the basic HTTP request codes: POST, GET.
      </Typography>
      <Typography variant="body1" paragraph>
        You can select the required response and request code language by selecting it from the drop-down menu on the top.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Authorization:</strong> Bearer
      </Typography>
      <Typography variant="body1" paragraph>
        All the APIs are provided with their appropriate example requests and responses for successful and failed calls. The example definitions are:
      </Typography>
      <Box pl={2}>
        <Typography variant="body1" paragraph>
          <strong>Successful Call:</strong> The API call was correct.
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>Invalid Data:</strong> The data entered was incorrect.
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>Missing Fields:</strong> Some of the required fields are missing.
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>Wrong Format:</strong> There is a syntax error in the code.
        </Typography>
      </Box>

      <Typography variant="h5" gutterBottom>
        Errors and Response Codes
      </Typography>
      <Typography variant="body1" paragraph>
        While using these APIs, you may run into some standard response and error codes. The most common ones are listed along with their descriptions as follows:
      </Typography>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><strong>Response Code</strong></TableCell>
              <TableCell><strong>Description</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>200 & 202 - OK & Accepted</TableCell>
              <TableCell>Everything worked as expected, and you'll get a response. Some APIs may respond with an error message if data is invalid.</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>400 - Bad Request</TableCell>
              <TableCell>The request was invalid or cannot be otherwise served.</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>401 - Unauthorized</TableCell>
              <TableCell>There is some error during validation. You need to check your token or credentials.</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>404 - Not Found</TableCell>
              <TableCell>The URI requested is invalid or the resource requested does not exist.</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>405 - Method Not Allowed</TableCell>
              <TableCell>The API was accessed using the wrong method. Check your HTTP method.</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>422 - Unprocessable Entity</TableCell>
              <TableCell>The request contains incorrect syntax or cannot be fulfilled. Try checking your code for errors.</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>429 - Too Many Requests</TableCell>
              <TableCell>You have exceeded the API call rate limit.</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>500, 502, 503, 504 - Server Errors</TableCell>
              <TableCell>Some server error has occurred. Some APIs may show this due to syntax or parameter errors. Try contacting support if this persists.</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="h5" gutterBottom>
        Support
      </Typography>
      <Typography variant="body1" paragraph>
        All the required information on integration is in this document. Please read it thoroughly.
      </Typography>
    </Box>
  );
};

export default CustomerLoyaltyAPIDocumentation;
